import { useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  Loader,
  DatePicker,
  IInstitution,
  IInstitutionHierarchies,
  AssignmentFilterCheckbox,
  Button,
  getSchoolIdFromHierarchy,
  CheckBoxGroup,
  ICheckedData,
  Group,
} from '@ascd/witsby-components';

import { Box, Grid } from '@mui/material';
import { forEach, get, isEmpty, orderBy, uniqBy } from 'lodash';
import ReactSelect, { CSSObjectWithLabel } from 'react-select';
import { ASCD_ID } from '@constants';
import { AppContext } from '@contexts/appContext';
import { DrawerWidthContext } from '@contexts/drawerContext';
import {
  DashboardContext,
  ISelectedInstitution,
  eDashboardActionType,
} from '@contexts/dashboardContext';

import GET_HIERARCHIES_FROM_SCHOOL_ID from '@graphql/schema/getHierarchiesFromSchool.graphql';
import GET_GROUPS from '@graphql/schema/groups.graphql';
import INSTITUTIONS from '@graphql/schema/institutions.graphql';
import UPDATE_USER_DETAILS from '@graphql/schema/mutations/updateUserDetails.graphql';
import AdminDashboardSidebarAssignedOrSelfAssigned from './AssignedOrSelfAssigned';
import AdminDashboardSidebarCollectionTypes from './CollectionTypes';
import AdminDashboardSidebarCreatedBy from './CreatedBy';

interface IAdminDashboardSidebar {
  onShowResultClick?: () => void;
}

const AdminDashboardSidebar = ({ onShowResultClick = () => null }: IAdminDashboardSidebar) => {
  const sideBarRef = useRef<HTMLDivElement>(null);
  const {
    state: {
      currentInstitution,
      currentUser,
      currentUserRole: { isAscdAdmin },
    },
  } = useContext(AppContext);

  const {
    state: { sideBarFilter },
    dispatch,
  } = useContext(DashboardContext);

  // Handle start date and end date filter
  const {
    dates: { startDate, endDate },
  } = sideBarFilter;
  const { drawerWidth } = useContext(DrawerWidthContext);

  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const sectionThreshold = sideBarRef?.current?.offsetHeight
        ? sideBarRef?.current?.offsetHeight
        : 660; // Adjust this threshold based on your design

      setIsScrolled(scrollTop > sectionThreshold);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleDateChange = useCallback(
    (value: string, key: string) => {
      if (!key) return;
      dispatch({
        type: 'SIDEBAR_FILTER' as eDashboardActionType.SIDEBAR_FILTER,
        data: {
          ...sideBarFilter,
          dates: {
            endDate,
            startDate,
            [key]: value,
          },
        },
      });
    },
    [dispatch, sideBarFilter, startDate, endDate],
  );

  // Handle institution selection filter for ASCD admins
  const { selectedInstitution } = sideBarFilter;

  const currentSelectedInstitution = useMemo(() => {
    if (!isEmpty(selectedInstitution)) return selectedInstitution;

    return {
      value: currentInstitution._id,
      label: currentInstitution.name,
    };
  }, [currentInstitution._id, currentInstitution.name, selectedInstitution]);

  const [getInstitutions, { data: institutionsData, loading: institutionsLoading }] = useLazyQuery(
    INSTITUTIONS,
    {
      variables: {
        filter: {
          page: 0,
          limit: 10000000,
        },
      },
      fetchPolicy: 'network-only',
    },
  );

  const institutions = get(institutionsData, 'institutions.institutions', []);
  const institutionsOption = useMemo(() => {
    if (isEmpty(institutions)) return [];
    const updatedArray = orderBy(
      institutions.map((item: IInstitution) => ({
        value: item._id,
        label: item.name,
        type: item.subscriberType,
        hierarchiesDataList: item.hierarchies,
        structure: item.structure,
      })),
      ['label'],
      ['asc'],
    );
    updatedArray.unshift({ value: '', label: 'ALL', isDisabled: false });
    return updatedArray;
  }, [institutions]);

  useEffect(() => {
    if (currentInstitution?._id && ASCD_ID === currentInstitution._id && isAscdAdmin) {
      getInstitutions();
    }
  }, [currentInstitution._id, getInstitutions, isAscdAdmin]);

  const assignmentFilterData: IInstitution = useMemo(() => {
    const selectedInstitute = institutions?.find(
      (item: IInstitution) => item._id === currentSelectedInstitution.value,
    );
    return !isAscdAdmin && ASCD_ID !== currentInstitution._id
      ? currentInstitution
      : selectedInstitute;
  }, [currentInstitution, institutions, isAscdAdmin, currentSelectedInstitution]);

  // get hierarchies based on current user school id
  const { loading } = useQuery(GET_HIERARCHIES_FROM_SCHOOL_ID, {
    variables: {
      schoolIds: [currentUser?.schoolId],
    },
    fetchPolicy: 'network-only',
    onCompleted(data) {
      const hierarchiesDataList: IInstitutionHierarchies[] = get(
        data,
        'getHierarchiesFromSchools',
        [],
      );

      if (
        isEmpty(currentUser?.preferences?.dashboardFilter) &&
        hierarchiesDataList &&
        hierarchiesDataList.length > 0 &&
        hierarchiesDataList[0]
      ) {
        const SArr = [];
        const HArr = [];

        for (const y of hierarchiesDataList) {
          if (y.parentId) {
            HArr.push(y.parentId);
          }
          if (y.structureId) {
            SArr.push(y.structureId);
          }
        }

        const schoolDta = hierarchiesDataList?.filter((item) =>
          currentInstitution?.structure?.some(
            (it) => item.structureId && it.id && item.structureId === it.id && it.type === 'SCHOOL',
          ),
        );

        dispatch({
          type: 'UPDATE_SIDEBAR_FILTER' as eDashboardActionType.UPDATE_SIDEBAR_FILTER,
          data: {
            includeHParentId: HArr,
            includeSParentId: SArr,
            internalId1: schoolDta,
            checkedList: hierarchiesDataList,
          },
        });

        dispatch({
          type: 'UPDATE_SIDEBAR_API_FILTER' as eDashboardActionType.UPDATE_SIDEBAR_API_FILTER,
          data: {
            includeHParentId: HArr,
            includeSParentId: SArr,
            internalId1: schoolDta,
            checkedList: hierarchiesDataList,
          },
        });
      }
    },
  });

  // Saving filter mutation
  const [updateUserDetailsQuery] = useMutation(UPDATE_USER_DETAILS, {
    fetchPolicy: 'network-only',
  });

  const handleShowResult = () => {
    onShowResultClick();
    dispatch({
      type: 'UPDATE_SIDEBAR_API_FILTER' as eDashboardActionType.UPDATE_SIDEBAR_API_FILTER,
      data: {
        ...sideBarFilter,
      },
    });
    updateUserDetailsQuery({
      variables: {
        updateUserDetailsInput: {
          oktaId: currentUser.oktaId,
          preferences: {
            ...currentUser.preferences,
            dashboardFilter: sideBarFilter,
          },
        },
      },
    });
  };

  // Handle selected hierarchies data
  const {
    checkedList,
    internalId1,
    includeSParentId,
    includeHParentId,
    selectedSchoolIds,
    selectedGroup,
    groupCheckList,
    checkedGroupAcrossSchool,
  } = sideBarFilter;

  // Fetching group of specific school
  const [groupQuery, { loading: instituteGroupLoading, data: instituteGroupData }] = useLazyQuery(
    GET_GROUPS,
    {
      fetchPolicy: 'network-only',
    },
  );

  useEffect(() => {
    if (
      internalId1
        .filter((val) => checkedList.some((el) => val.id === el.id))
        .map((item) => item.internalId).length > 0
    ) {
      groupQuery({
        variables: {
          parentId: internalId1
            .filter((val) => checkedList.some((el) => val.id === el.id))
            .map((item) => item.internalId),
          filter: {
            limit: 10000,
            page: 0,
          },
        },
      });
    }
  }, [checkedList, groupQuery, internalId1]);

  const groupInstituteData = get(instituteGroupData, 'groups.groups', []);

  //  Fetching group across school data
  const { data: groupAcrossSchoolDataList, loading: groupAcrossSchoolLoading } = useQuery(
    GET_GROUPS,
    {
      variables: {
        organizationId: assignmentFilterData?._id,
        parentId: null,
        filter: {
          limit: 10000,
          page: 0,
        },
      },
      fetchPolicy: 'network-only',
    },
  );

  const groupAcrossSchoolList = get(groupAcrossSchoolDataList, 'groups.groups', []);

  useEffect(() => {
    const filteredData = selectedGroup.filter((item) =>
      internalId1
        .filter((val) => checkedList.some((el) => val.id === el.id))
        ?.some((it) => item?.parentId === it.internalId),
    );
    const groupAcross = selectedGroup.filter((item) => item?.parentId === null);
    dispatch({
      type: 'SIDEBAR_FILTER' as eDashboardActionType.SIDEBAR_FILTER,
      data: {
        ...sideBarFilter,
        groupCheckList: groupCheckList.filter((gFItem) =>
          internalId1
            .filter((val) => checkedList.some((el) => val.id === el.id))
            ?.some((it) => gFItem.parentId === it.internalId),
        ),
        selectedGroup: [...filteredData, ...groupAcross],
      },
    });
  }, [checkedList, internalId1]);

  return (
    <Box ref={sideBarRef} sx={{ p: 2 }}>
      {/* Start and End date selection filter */}
      <Grid container spacing={2} sx={{ '.MuiGrid-item': { flexBasis: '100%' } }}>
        <Grid item xs={12}>
          <Box
            sx={(theme) => ({
              p: 2,
              border: '1px solid',
              borderColor: theme.palette.grey[500],
              borderRadius: theme.border.radius.REGULAR,
              '& .MuiTextField-root': {
                width: '60%',
              },
              '& .css-1fkmw0i': {
                background: 'transparent',
                p: 0,
              },
            })}>
            <Box>
              <DatePicker
                value={startDate}
                label="Start Date"
                format="MM/dd/yyyy"
                handleOnChangeDate={(value) => handleDateChange(value, 'startDate')}
              />
            </Box>
            <Box sx={{ mt: 1.5 }}>
              <DatePicker
                value={endDate}
                label="End Date"
                format="MM/dd/yyyy"
                handleOnChangeDate={(value) => handleDateChange(value, 'endDate')}
              />
            </Box>
          </Box>
        </Grid>
        {/* Institution selection filter for ASCD Admins */}
        {ASCD_ID === currentInstitution?._id && isAscdAdmin && (
          <Grid item xs={12}>
            <Box
              sx={(theme) => ({
                p: 2,
                border: '1px solid',
                borderColor: theme.palette.grey[500],
                borderRadius: theme.border.radius.REGULAR,
              })}>
              <Box
                sx={(theme) => ({
                  mb: 0.5,
                  color: theme.palette.grey[50],
                  fontSize: theme.font.size.REGULAR,
                  fontWeight: theme.font.weight.BOLD,
                })}>
                Institutions:
              </Box>
              <ReactSelect
                isSearchable
                isLoading={institutionsLoading}
                placeholder="Select Institution"
                value={currentSelectedInstitution}
                onChange={(values) => {
                  const schoolDataId = (values?.hierarchiesDataList || [])?.filter((item) =>
                    (values?.structure || [])?.some(
                      (it) =>
                        item.structureId &&
                        it.id &&
                        item.structureId === it.id &&
                        it.type === 'SCHOOL' &&
                        item.internalId,
                    ),
                  );

                  let allInstitutionsId;
                  let createdByOrganizationId = [values?.value as string, ASCD_ID];

                  if (values?.label === 'ALL') {
                    createdByOrganizationId = institutionsOption.map(
                      (institution) => institution.value,
                    );
                    allInstitutionsId = createdByOrganizationId;
                  }

                  dispatch({
                    type: 'SIDEBAR_FILTER' as eDashboardActionType.SIDEBAR_FILTER,
                    data: {
                      dates: {
                        endDate,
                        startDate,
                      },
                      includeHParentId: [],
                      includeSParentId: [],
                      assignedBy: ['ASSIGNED', 'SELF_ENROLLED'],
                      internalId1: schoolDataId as IInstitutionHierarchies[],
                      selectedSchoolIds: [] as IInstitutionHierarchies[],
                      checkedList: [] as IInstitutionHierarchies[],
                      selectedInstitution: values as ISelectedInstitution,
                      createdByOrganizationId,
                      allInstitutionsId,
                      selectedGroup: [] as Group[],
                      groupCheckList: [] as Group[],
                      checkedGroupAcrossSchool: [] as ICheckedData[],
                    },
                  });
                }}
                options={institutionsOption || []}
                styles={{
                  menu: (provided) => ({ ...provided, zIndex: 9999 }) as CSSObjectWithLabel,
                  valueContainer: (styles) =>
                    ({
                      ...styles,
                      minWidth: 90,
                      width: '11.25rem',
                    }) as CSSObjectWithLabel,
                }}
              />
            </Box>
          </Grid>
        )}

        {/* District, Schools and Groups Filter */}
        {loading ? (
          <Grid item xs={12}>
            <Box
              sx={{
                height: '16rem',
                // width: '16rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Loader size={25} />
            </Box>
          </Grid>
        ) : (
          <AssignmentFilterCheckbox
            defaultDrawerWidth={drawerWidth}
            // isIndividualCheck
            isFilterEnabled
            groupData={groupInstituteData}
            groupDataLoading={instituteGroupLoading}
            checkedGroupList={groupCheckList}
            checkedList={checkedList}
            assignmentFilterData={assignmentFilterData}
            includeStructureParentId={includeSParentId}
            includeHierarchiesParentId={includeHParentId}
            selectedAllGroup={
              !instituteGroupLoading &&
              groupInstituteData.length > 0 &&
              groupInstituteData.length === groupCheckList.length
            }
            schoolList={internalId1.filter((val) => checkedList.some((el) => val.id === el.id))}
            showLevel={includeHParentId.length > 0 ? includeHParentId.length + 1 : 2}
            handleSelectAll={(selectAllData, parentId, level, type, checked) => {
              if (checked) {
                const hierarchyIds = [
                  ...includeHParentId,
                  ...selectAllData
                    .filter((val) => !includeHParentId.includes(val.id))
                    .map((val) => val.id),
                ];

                dispatch({
                  type: 'SIDEBAR_FILTER' as eDashboardActionType.SIDEBAR_FILTER,
                  data: {
                    ...sideBarFilter,
                    checkedList: [
                      ...checkedList,
                      ...selectAllData.filter((val) => !checkedList.some((el) => el.id === val.id)),
                    ],
                    includeSParentId: [...includeSParentId, parentId],
                    includeHParentId: [
                      ...includeHParentId,
                      ...selectAllData
                        .filter((val) => !includeHParentId.includes(val.id))
                        .map((val) => val.id),
                    ],
                    internalId1: getSchoolIdFromHierarchy(
                      hierarchyIds,
                      assignmentFilterData.hierarchies,
                      assignmentFilterData.structure,
                    ),
                    selectedSchoolIds:
                      getSchoolIdFromHierarchy(
                        hierarchyIds,
                        assignmentFilterData.hierarchies,
                        assignmentFilterData.structure,
                      ) || [],
                  },
                });
              } else {
                const structures = assignmentFilterData.structure;
                const currentLevel = level;
                const excludeStructureIds: string[] = [];
                structures.forEach((structure) => {
                  if (structure.level >= (currentLevel as number)) {
                    excludeStructureIds.push(structure.id);
                  }
                });

                const hierarchyIds = checkedList
                  .filter((el) => !excludeStructureIds.includes(el.structureId))
                  .map((val) => val.id);

                dispatch({
                  type: 'SIDEBAR_FILTER' as eDashboardActionType.SIDEBAR_FILTER,
                  data: {
                    ...sideBarFilter,
                    includeHParentId: checkedList
                      .filter((el) => !excludeStructureIds.includes(el.structureId))
                      .map((val) => val.id),
                    includeSParentId: includeSParentId.filter(
                      (val) => !excludeStructureIds.includes(val),
                    ),
                    checkedList: checkedList.filter(
                      (val) => !excludeStructureIds.includes(val.structureId),
                    ),
                    internalId1: getSchoolIdFromHierarchy(
                      hierarchyIds,
                      assignmentFilterData.hierarchies,
                      assignmentFilterData.structure,
                    ),
                    selectedSchoolIds:
                      internalId1.filter((item) =>
                        selectAllData
                          .filter(
                            (fItem) =>
                              fItem.type === 'SCHOOL' &&
                              !excludeStructureIds.includes(fItem.structureId),
                          )
                          .some((el) => el.id !== item.id),
                      ) || [],
                  },
                });
              }
            }}
            handleCheck={(parentId, childId, internalId, type, title, hierarchieData) => {
              const schoolData = assignmentFilterData?.structure.find((v) => v.type === 'SCHOOL');
              let tempInternalId1 = internalId1;
              let tempSchoolIds = selectedSchoolIds;
              const structures = assignmentFilterData.structure;
              const { hierarchies } = assignmentFilterData;
              const isFirstCheck = !checkedList.some(
                (v) => v.structureId === hierarchieData.structureId,
              );

              function getChildrenByStructureId(
                data: IInstitutionHierarchies[],
                structureId: string,
                initialIds: string[],
              ): IInstitutionHierarchies[] {
                let children: IInstitutionHierarchies[] = [];

                const sId = structures.find((m) => m.parentId === structureId)?.id;

                for (const idData of initialIds) {
                  const node = data.filter((item) => item.parentId === idData);
                  if (node && sId && node.map((e) => e?.structureId).includes(sId)) {
                    children = [...children, ...node];
                  }

                  const directChildren = data.filter((item) => item.parentId === idData);
                  if (directChildren.length > 0) {
                    children = children.concat(
                      getChildrenByStructureId(
                        data,
                        sId as string,
                        directChildren.map((child) => child.id),
                      ),
                    );
                  }
                }

                // Remove duplicates
                children = children
                  .filter((v, i, a) => a.findIndex((t) => t.id === v.id) === i)
                  .filter((v) => v.internalId);

                return children;
              }
              const idsList = checkedList
                .filter((v) => v.structureId === parentId)
                .map((e) => e.id);

              if (type === 'SCHOOL') {
                if (isFirstCheck) {
                  tempInternalId1 = internalId1.filter((item) => item.id === hierarchieData.id);
                } else if (internalId1.filter((item) => item.id === hierarchieData.id).length > 0) {
                  tempInternalId1 = internalId1.filter((item) => item.id !== hierarchieData.id);
                } else if (hierarchieData?.internalId) {
                  tempInternalId1 = [...internalId1, hierarchieData];
                } else {
                  tempInternalId1 = [...internalId1];
                }
                tempSchoolIds = tempInternalId1;
              } else {
                tempSchoolIds = [];
                tempInternalId1 = checkedList.some(
                  (m) => m.structureId === structures.find((v) => v.parentId === parentId)?.id,
                )
                  ? tempInternalId1
                  : getChildrenByStructureId(hierarchies, parentId, [...idsList, childId]);
              }
              if (!checkedList?.some((item) => item.id === hierarchieData.id)) {
                if (type !== 'SCHOOL') {
                  tempSchoolIds = [...checkedList, hierarchieData].filter(
                    (v) => v.structureId === schoolData?.id,
                  );
                }
                dispatch({
                  type: 'SIDEBAR_FILTER' as eDashboardActionType.SIDEBAR_FILTER,
                  data: {
                    ...sideBarFilter,
                    internalId1: tempInternalId1,
                    selectedSchoolIds: tempSchoolIds,
                    checkedList: [...checkedList, hierarchieData],
                    includeHParentId: [...includeHParentId, childId],
                    includeSParentId: [...includeSParentId, parentId],
                  },
                });
                return;
              }

              if (checkedList.filter((val) => val.id === hierarchieData.id).length > 0) {
                const currentHierarchyId = hierarchieData.id;
                const currentStructureId = hierarchieData.structureId;
                const currentLevel = structures.find(
                  (structure) => structure.id === currentStructureId,
                )?.level;
                const hierarchyByStructureId: { [key: string]: IInstitutionHierarchies[] } = {};
                const excludeStructureIds: string[] = [];
                const excludeStructureParentsIds: string[] = [];

                structures.forEach((structure) => {
                  if (structure.level >= (currentLevel as number)) {
                    excludeStructureIds.push(structure.id);
                    excludeStructureParentsIds.push(structure.parentId);
                    hierarchyByStructureId[`${structure.id}`] = hierarchies.filter(
                      (hierarchy) => hierarchy.structureId === structure.id,
                    );
                  }
                });

                if (hierarchyByStructureId[`${currentStructureId}`].length > 1) {
                  const parentIds: string[] = [];

                  forEach(hierarchyByStructureId, (hierarchyByStructureIdData, key) => {
                    if (key !== currentStructureId) {
                      hierarchyByStructureIdData.forEach((h: IInstitutionHierarchies) => {
                        if (h.parentId === currentHierarchyId || parentIds.includes(h.parentId)) {
                          parentIds.push(h.id);
                        }
                      });
                    }
                  });

                  let tempId: IInstitutionHierarchies[];

                  if (
                    type === 'SCHOOL' &&
                    !isFirstCheck &&
                    checkedList.filter((e) => e.structureId === parentId).length === 1
                  ) {
                    return;
                  }
                  if (type === 'SCHOOL') {
                    tempId = checkedList.filter(
                      (k) => k.structureId === parentId && k.id !== childId && k.internalId,
                    );
                  } else if (
                    checkedList.filter((e) => e.structureId === hierarchieData.structureId)
                      .length === 1
                  ) {
                    const structId = structures.find((v) => v.id === parentId)?.parentId;
                    const ids = hierarchies
                      .filter((m) => m.structureId === structId)
                      ?.map((k) => k.id);
                    tempId = getChildrenByStructureId(hierarchies, structId || '', [...ids]);
                  } else {
                    const ids = checkedList
                      .filter((m) => m.structureId === parentId)
                      ?.map((k) => k.id)
                      .filter((id) => id !== hierarchieData.id);
                    tempId = getChildrenByStructureId(hierarchies, parentId || '', [...ids]);
                  }
                  const updatedCheckedList = checkedList
                    .filter((checkedAssignment) => !parentIds.includes(checkedAssignment.id))
                    .filter((checkedAssignment) => checkedAssignment.id !== currentHierarchyId);
                  if (type !== 'SCHOOL') {
                    tempSchoolIds = [...updatedCheckedList].filter(
                      (v) => v.structureId === schoolData?.id,
                    );
                  }
                  dispatch({
                    type: 'SIDEBAR_FILTER' as eDashboardActionType.SIDEBAR_FILTER,
                    data: {
                      ...sideBarFilter,
                      ...(includeSParentId.length && {
                        includeSParentId: [
                          ...includeSParentId.filter((val) => !excludeStructureIds.includes(val)),
                          currentStructureId,
                        ],
                      }),
                      includeHParentId: includeHParentId
                        .filter((val) => !parentIds.includes(val))
                        .filter((val) => val !== currentHierarchyId),
                      checkedList: updatedCheckedList,
                      internalId1: tempId,
                      selectedSchoolIds: tempSchoolIds || [],
                    },
                  });
                } else {
                  const parentIds: string[] = [];

                  forEach(hierarchyByStructureId, (hierarchyByStructureIdData, key) => {
                    if (key !== currentStructureId) {
                      hierarchyByStructureIdData.forEach((h: IInstitutionHierarchies) => {
                        if (h.parentId === currentHierarchyId || parentIds.includes(h.parentId)) {
                          parentIds.push(h.id);
                        }
                      });
                    }
                  });

                  let tempIds: IInstitutionHierarchies[];
                  if (
                    type === 'SCHOOL' &&
                    !isFirstCheck &&
                    checkedList.filter((e) => e.structureId === parentId).length === 1
                  ) {
                    return;
                  }

                  if (
                    checkedList.filter((e) => e.structureId === hierarchieData.structureId)
                      .length === 1 &&
                    currentLevel !== 1
                  ) {
                    const structId = structures.find((v) => v.id === parentId)?.parentId;
                    const ids = hierarchies
                      .filter((m) => m.structureId === structId)
                      ?.map((k) => k.id);
                    tempIds = getChildrenByStructureId(hierarchies, structId || '', [...ids]);
                  } else {
                    tempIds = internalId1.filter(
                      (val) => !excludeStructureParentsIds.includes(val.structureId),
                    );
                  }
                  const updatedCheckedList = checkedList.filter(
                    (checkedAssignment) =>
                      !excludeStructureIds.includes(checkedAssignment.structureId),
                  );
                  if (type !== 'SCHOOL') {
                    tempSchoolIds = [...updatedCheckedList].filter(
                      (v) => v.structureId === schoolData?.id,
                    );
                  }
                  dispatch({
                    type: 'SIDEBAR_FILTER' as eDashboardActionType.SIDEBAR_FILTER,
                    data: {
                      ...sideBarFilter,
                      ...(includeSParentId.length && {
                        includeSParentId: includeSParentId.filter(
                          (val) => !excludeStructureIds.includes(val),
                        ),
                      }),
                      includeHParentId: includeHParentId
                        .filter((val) => !parentIds.includes(val))
                        .filter((val) => val !== currentHierarchyId),
                      checkedList: updatedCheckedList,
                      internalId1: tempIds,
                      selectedSchoolIds: tempSchoolIds || [],
                    },
                  });
                }
              }
            }}
            handleGroupCheck={(schoolId?: string, schoolName?: string, groupValue?: Group) => {
              if (
                groupValue &&
                groupCheckList.filter((gFItem) => gFItem?._id === groupValue?._id).length === 0
              ) {
                if (!groupCheckList?.some((item) => item?._id === groupValue?._id)) {
                  dispatch({
                    type: 'SIDEBAR_FILTER' as eDashboardActionType.SIDEBAR_FILTER,
                    data: {
                      ...sideBarFilter,
                      groupCheckList: [...groupCheckList, groupValue],
                      selectedGroup: [...selectedGroup, groupValue],
                    },
                  });
                }
                return;
              }
              if (
                groupValue &&
                groupCheckList.filter((gFItem) => gFItem?._id === groupValue?._id).length > 0
              ) {
                if (selectedGroup?.some((item) => item?._id === groupValue?._id)) {
                  dispatch({
                    type: 'SIDEBAR_FILTER' as eDashboardActionType.SIDEBAR_FILTER,
                    data: {
                      ...sideBarFilter,
                      selectedGroup: selectedGroup.filter((g1) => g1?._id !== groupValue?._id),
                      groupCheckList: groupCheckList.filter(
                        (gFItem) => gFItem?._id !== groupValue?._id,
                      ),
                    },
                  });
                }

                // return;
              }
            }}
            handleSelectAllForGroup={(allGroupVal, checked) => {
              if (checked) {
                dispatch({
                  type: 'SIDEBAR_FILTER' as eDashboardActionType.SIDEBAR_FILTER,
                  data: {
                    ...sideBarFilter,
                    groupCheckList: uniqBy([...groupCheckList, ...allGroupVal], '_id'),
                    selectedGroup: uniqBy([...selectedGroup, ...allGroupVal], '_id'),
                  },
                });
              } else {
                dispatch({
                  type: 'SIDEBAR_FILTER' as eDashboardActionType.SIDEBAR_FILTER,
                  data: {
                    ...sideBarFilter,
                    groupCheckList: groupCheckList.filter(
                      (val) => !allGroupVal.some((el) => el?._id === val?._id),
                    ),
                    selectedGroup: selectedGroup.filter(
                      (val) => !allGroupVal.some((el) => el?._id === val?._id),
                    ),
                  },
                });
              }
            }}
          />
        )}

        {/* Group Across School  */}

        {groupAcrossSchoolLoading && (
          <Grid item xs={12}>
            <Box
              sx={{
                height: '16rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              {' '}
              <Loader size={25} />
            </Box>
          </Grid>
        )}
        {groupAcrossSchoolList.length > 0 && (
          <Grid item xs={12}>
            <CheckBoxGroup
              defaultDrawerWidth={drawerWidth}
              isSearchEnable
              isSelectAllEnable
              boxSx={{
                maxWidth: 'none',
                minHeight: '16rem',
                maxHeight: '16rem',
                overflowY: 'scroll',
                wordBreak: 'break-word',
              }}
              headerLabel="Groups across schools"
              checkedData={groupAcrossSchoolList?.map((item: ICheckedData) => ({
                _id: item?._id,
                name: item.name,
              }))}
              checkedList={checkedGroupAcrossSchool}
              handleSelectAll={(val, checked) => {
                if (checked) {
                  const checkedGroup: Group[] = (groupAcrossSchoolList || [])?.filter(
                    (filterGroup: Group) => val.some((el) => el._id === filterGroup._id),
                  );
                  dispatch({
                    type: 'SIDEBAR_FILTER' as eDashboardActionType.SIDEBAR_FILTER,
                    data: {
                      ...sideBarFilter,
                      checkedGroupAcrossSchool: uniqBy(
                        [...(checkedGroupAcrossSchool || []), ...val],
                        '_id',
                      ),
                      selectedGroup: uniqBy([...selectedGroup, ...checkedGroup], '_id'),
                    },
                  });
                } else {
                  dispatch({
                    type: 'SIDEBAR_FILTER' as eDashboardActionType.SIDEBAR_FILTER,
                    data: {
                      ...sideBarFilter,
                      checkedGroupAcrossSchool: checkedGroupAcrossSchool.filter(
                        (el) => !val.some((c) => c._id === el._id),
                      ),
                      selectedGroup: selectedGroup.filter(
                        (el) => !val.some((c) => c._id === el._id),
                      ),
                    },
                  });
                }
              }}
              handleCheck={(id, name) => {
                if (
                  (checkedGroupAcrossSchool || [])?.filter((item) => item?._id === id).length > 0
                ) {
                  dispatch({
                    type: 'SIDEBAR_FILTER' as eDashboardActionType.SIDEBAR_FILTER,
                    data: {
                      ...sideBarFilter,
                      checkedGroupAcrossSchool: checkedGroupAcrossSchool?.filter(
                        (item) => item?._id !== id,
                      ),
                      selectedGroup: selectedGroup?.filter(
                        (filterGroup: Group) => filterGroup?._id !== id,
                      ),
                    },
                  });
                } else {
                  dispatch({
                    type: 'SIDEBAR_FILTER' as eDashboardActionType.SIDEBAR_FILTER,
                    data: {
                      ...sideBarFilter,
                      checkedGroupAcrossSchool: [
                        ...(checkedGroupAcrossSchool || []),
                        { _id: id, name },
                      ],
                      selectedGroup: [
                        ...selectedGroup,
                        groupAcrossSchoolList?.find(
                          (filterGroup: Group) => filterGroup?._id === id,
                        ),
                      ],
                    },
                  });
                }
              }}
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <AdminDashboardSidebarCreatedBy />
        </Grid>
        {/* Assigned Or SelfAssigned Filter */}
        <Grid item xs={12}>
          <AdminDashboardSidebarAssignedOrSelfAssigned />
        </Grid>
        {/* Collection Type Filter */}
        <Grid item xs={12}>
          <AdminDashboardSidebarCollectionTypes />
        </Grid>
      </Grid>
      <Box
        sx={{
          marginY: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'sticky',
          bottom: '30px',
          zIndex: 1000,
          ...(isScrolled && {
            position: 'static',
          }),
        }}>
        <Button
          secondary
          sx={{
            px: 4,
            py: 0.5,
            '&:hover': {
              transform: 'scale(1.1)',
            },
          }}
          onClick={() => handleShowResult()}>
          Show results
        </Button>
      </Box>
    </Box>
  );
};

export default AdminDashboardSidebar;
